<template>
  <div class="main__content content">
    <div class="pacs-settings-table">
      <div class="pacs-settings-table__group-setting">
        <SmartInputText
          v-model="searchText"
          :caption="$t('search')"
          class="pacs-settings-table__settings-control pacs-settings-table__settings-control_big"
          size="l"
          @keyup.native.enter="applyTableSettings()"
        >
          <CamsButton
            position="r"
            size="l"
            type="button"
            icon-type="only"
            @click="applyTableSettings()"
          >
            <svg class="icon-search">
              <use xlink:href="#icon-search" />
            </svg>
          </CamsButton>
        </SmartInputText>
        <SmartInputDate
          v-model="archiveFrom"
          :caption="$t('startDate')"
          class="pacs-settings-table__settings-control pacs-settings-table__settings-control_small"
          size="l"
          :config-flat-pickr="{minDate: minEventArchiveFrom, maxDate: new Date()}"
          @input="applyTableSettings()"
        />
        <SmartInputDate
          v-model="archiveTo"
          :caption="$t('endDate')"
          class="pacs-settings-table__settings-control pacs-settings-table__settings-control_small"
          size="l"
          :config-flat-pickr="{minDate: archiveFrom, maxDate: new Date()}"
          @input="applyTableSettings()"
        />
        <SmartInputText
          v-model="minTemperature"
          :caption="$t('minTemperature')"
          class="pacs-settings-table__settings-control"
          input-type="number"
          size="l"
          step="0.1"
          @input="applyTableSettings()"
        />
        <SmartInputText
          v-model="maxTemperature"
          :caption="$t('maxTemperature')"
          class="pacs-settings-table__settings-control"
          input-type="number"
          size="l"
          step="0.1"
          @input="applyTableSettings()"
        />
        <CamsButton
          class="pacs-settings-table__settings-control"
          priority="primary"
          size="l"
          type="button"
          @click="openDownloadReportScreen()"
        >
          {{ $t('downloadReport') }}
        </CamsButton>
        <SmartSelect
          v-model="pageSize"
          :options="optionsPageSize"
          :caption="$t('itemsPerPage')"
          class="pacs-settings-table__settings-control"
          size="s"
          @change="applyTableSettings"
        />
      </div>
    </div>

    <SpinnerLoading v-if="isLoading" class="loader_center" color="blue" />

    <template v-else>
      <p v-show="dataRows.length === 1000" class="pacs-settings-table__warn">
        {{ $t('tooManyRows') }}
      </p>
      <PacsTable
        :column-captions="{
          text: 'name',
          cameraNumber: 'cameraNumber',
          date: 'date',
          temperature: 'temperature',
          alarm: 'alarm',
        }"
        :column-names="columnNames"
        :data-rows="dataRows"
        :page-count="pageCount"
        :total-count="dataRows.length"
        :view-only-mode="true"
        :current-page="currentPage"
        @new-page="selectPage"
      >
        <template #customThCells />
        <template #customTdCells="{dataRow}">
          <CamsButton
            icon-type="only"
            :title="$t('screenshotEvent')"
            type="button"
            @click="showFullScreenshotForEventInTable(dataRow['_analyticMessage_'])"
          >
            <svg>
              <use xlink:href="#icon-screen-preview" />
            </svg>
          </CamsButton>
          <CamsButton
            icon-type="only"
            :title="$t('eventVideo')"
            type="button"
            @click="playEventStartForEventInTable(dataRow['_analyticMessage_'])"
          >
            <svg>
              <use xlink:href="#icon-video-preview" />
            </svg>
          </CamsButton>
          <CamsButton
            icon-type="only"
            :title="$t('downloadEventVideo')"
            type="button"
            @click="downloadEventVideoInTable(dataRow['_analyticMessage_'])"
          >
            <svg>
              <use xlink:href="../../../assets/img/icons.svg#cloud-archive" />
            </svg>
          </CamsButton>
        </template>
      </PacsTable>
    </template>

    <!--iframe для скачивания-->
    <iframe :src="downloadUrl" frameborder="0" height="1" width="1" />
  </div>
</template>

<script>
import {
  ACTION_LOAD_PACS_THERMAL_VISION_REPORT
} from "@/store/pacs/analytics/index.js";
import PacsTable from "@/components/pacs/PacsTable.vue";
import {SPECIAL_TYPES_DATA_IN_CELLS} from "@/store/pacs/helpers.js";
import {eventTableMixin} from "@/components/pacs/analytics/mixins.js";

/**
 * Компонент отображения таблицы и фильтров с информацией по событиям тепловизора.
 */
export default {
  name: "TableThermalVision",
  mixins: [
    eventTableMixin
  ],
  components: {
    PacsTable
  },
  data() {
    return {
      columnNames: ["text", "cameraNumber", "date", "temperature", "alarm"],
      columnCaptions: {
        text: this.$t('name'),
        cameraNumber: this.$t('cameraNumber'),
        date: this.$t('date'),
        temperature: this.$t('temperature'),
        alarm: this.$t('alarm'),
      },
      archiveFrom: null,
      archiveTo: null,
      minTemperature: null,
      maxTemperature: null,
      searchText: null,
    };
  },
  computed: {
    /**
     * @return {Date} Корректное представление archiveFrom в сравнении с archiveTo.
     */
    clearArchiveFrom() {
      return this.archiveFrom && this.archiveTo ? new Date(Math.min(this.archiveFrom.getTime(), this.archiveTo.getTime())) : null;
    },
    /**
     * @return {Date} Корректное представление archiveTo в сравнении с archiveFrom.
     */
    clearArchiveTo() {
      return this.archiveFrom && this.archiveTo ? new Date(Math.max(this.archiveFrom.getTime(), this.archiveTo.getTime())) : null;
    },
  },
  methods: {
    selectPage(page) {
      this.currentPage = page; // Устанавливаем текущую страницу
      this.applyTableSettings(); // Применяем фильтры и загружаем данные для новой страницы
    },
    /**
     * @see eventTableMixin.methods.applyTableSettings
     */
    applyTableSettings() {
      this.stringifyQueryParams({
        f: +this.clearArchiveFrom,
        t: +this.clearArchiveTo,
        mint: this.minTemperature,
        maxt: this.maxTemperature,
        s: this.searchText,
        c: this.currentPage,
        p: this.pageSize,
      });
    },
    /**
     * @see eventTableMixin.methods.assignQueryParams
     */
    assignQueryParams(parsedQueryParams) {
      if (_.isEmpty(parsedQueryParams)) {
        this.resetFilters();
      } else {
        this.archiveFrom = new Date(parsedQueryParams.f);
        this.archiveTo = new Date(parsedQueryParams.t);
        this.minTemperature = parsedQueryParams.mint;
        this.maxTemperature = parsedQueryParams.maxt;
        this.searchText = parsedQueryParams.s;
        this.currentPage = parsedQueryParams.c;
        this.pageSize = parsedQueryParams.p;
      }
    },
    /**
     * @see eventTableMixin.methods.loadPage.
     */
    async loadPage() {
      if (!this.clearArchiveFrom || !this.clearArchiveTo) {
        return;
      }

      this.isLoading = true;
      let responseData;
      try {
        responseData = await this.$store.dispatch(`pacs/analytics/${ACTION_LOAD_PACS_THERMAL_VISION_REPORT}`, {
          archiveFrom: this.clearArchiveFrom,
          archiveTo: this.clearArchiveTo,
          minTemperature: this.minTemperature,
          maxTemperature: this.maxTemperature,
          query: this.searchText,
          page: this.currentPage,
          pageSize: this.pageSize,
        });
        this.currentPage = responseData.page.current || null;
        this.pageCount = responseData.page.all || null;
        this.pageSize = responseData.page.page_size || null;
      } catch (error) {
        if (!error) {
          return; // Для прерванных ранее отправленных запросов.
        }
        this.$camsdals.alert(this.$t('loadingError'));
      }
      responseData = responseData.results ?? [];
      this.dataRows = responseData.map((message) => {
        return {
          "text": {
            value: message.text || this.$t('unknown'),
          },
          "cameraNumber": {
            value: message.cameraNumber,
          },
          "date": {
            type: SPECIAL_TYPES_DATA_IN_CELLS.DATE_TIME,
            value: message.date,
          },
          "temperature": {
            value: message.temperature,
          },
          "alarm": {
            type: SPECIAL_TYPES_DATA_IN_CELLS.BOOLEAN,
            value: message.alarm,
          },
          "_analyticMessage_": message, // Объект сообщения для передачи в события клика в кнопках на строчках в таблице. См. в этом шаблоне.
        };
      });
      this.isLoading = false;

    },
    /**
     * Сброс фильтров в начальное состояние.
     * Фильтры по умолчанию по датам за последний час.
     */
    resetFilters() {
      const archiveTo = this.maxArchiveTo || new Date(),
            archiveFrom = new Date(archiveTo.getTime());
      archiveFrom.setHours(archiveTo.getHours() - 1);
      this.archiveFrom = archiveFrom;
      this.archiveTo = archiveTo;
      this.minTemperature = 20;
      this.maxTemperature = 60;
      this.searchText = "";
      this.currentPage = 1;
    },
    /**
     * Выгрузка данных таблицы в виде CSV.
     */
    downloadTableAsCSVReport() {
      const csvHeaders = [
        this.$t('name'),
        this.$t('cameraNumber'),
        this.$t('date'),
        this.$t('temperature'),
        this.$t('alarm')
      ];

      const formatDataForCSV = (message) => {
        return [
          message.text,
          message.cameraNumber,
          this.$options.filters.localDateTime(message.date),
          message.temperature,
          message.alarm ? this.$t('yes') : this.$t('no')
        ];
      };

      const filters = {};
      if (this.minTemperature != null) filters.minTemperature = this.minTemperature;
      if (this.maxTemperature != null) filters.maxTemperature = this.maxTemperature;
      if (this.searchText) filters.query = this.searchText;

      this.downloadTableAsCSV(
        `pacs/analytics/${ACTION_LOAD_PACS_THERMAL_VISION_REPORT}`,
        this.$t('thermalVisionReport'),
        csvHeaders,
        formatDataForCSV,
        filters
      );
    },
    downloadTableAsXLSXReport() {
      return this.downloadDefaultXLSXReport(
        "api:v0:pacs-analytics-thermal-vision-report",
        this.$t('thermalVisionReport'),
        {
          temperature_min: this.minTemperature,
          temperature_max: this.maxTemperature,
          query: this.searchText,
        }
      );
    }

  },
};
</script>
